import axios from 'axios';
import { getApiUrl } from '@/config/api';
import authService from '@/shared/services/auth';

export default class CatObjetoService {
  constructor() {
    this.objetos = [];
    this.loading = false;
    this.error = null;
    this.sortField = null;
    this.sortDirection = 'asc';
    this.currentPage = 1;
    this.itemsPerPage = 10;
    this.selectedEmpresa = null;
  }

  // Inicializar el servicio
  async initialize() {
    await this.waitForEmpresa();
    await this.listar();
  }

  // Esperar a que la empresa esté seleccionada
  async waitForEmpresa() {
    let attempts = 0;
    const maxAttempts = 50; // 5 segundos máximo
    
    while (attempts < maxAttempts) {
      this.selectedEmpresa = authService.getSelectedEmpresa();
      if (this.selectedEmpresa && this.selectedEmpresa.EmpresaId) {
        console.log('Empresa encontrada para CatObjeto:', this.selectedEmpresa);
        return;
      }
      
      // Esperar 100ms antes del siguiente intento
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    // Si no se encuentra empresa después de los intentos
    this.error = 'No se pudo obtener la empresa seleccionada';
    console.error('No se pudo obtener empresa para CatObjeto después de', maxAttempts, 'intentos');
  }

  // Listar objetos
  async listar() {
    this.loading = true;
    this.error = null;

    console.log('Cargando objetos de CatObjeto...');
    
    try {
      // Verificar que tenemos empresa seleccionada
      if (!this.selectedEmpresa || !this.selectedEmpresa.EmpresaId) {
        this.selectedEmpresa = authService.getSelectedEmpresa();
        if (!this.selectedEmpresa || !this.selectedEmpresa.EmpresaId) {
          this.loading = false;
          return { success: false, data: [], message: 'Debe elegir una empresa antes de poder continuar.' };
        }
      }

      const token = authService.getToken();
      if (!token) {
        throw new Error('No hay token de autenticación');
      }

      const response = await axios.post(getApiUrl('/api/CatObjeto/listar'), {
        EmpresaId: this.selectedEmpresa.EmpresaId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      
      // Filtrar por empresa en el frontend como medida de seguridad adicional
      const allObjetos = response.data || [];
      this.objetos = allObjetos.filter(objeto => objeto.EmpresaId === this.selectedEmpresa.EmpresaId);
      this.loading = false;
      
      console.log('Objetos cargados:', this.objetos.length, 'de', allObjetos.length, 'total');
      console.log('Empresa del usuario:', this.selectedEmpresa.EmpresaId);
      this.loading = false;
      
      return {
        success: true,
        data: this.objetos,
        message: 'Objetos cargados exitosamente'
      };
    } catch (error) {
      console.error('Error al cargar objetos:', error);
      this.error = 'No se pudieron cargar los objetos. Por favor, intente nuevamente.';
      this.loading = false;
      throw error;
    }
  }

  // Guardar objeto (insert/update)
  async guardar(objeto) {
    try {
      console.log('Guardando objeto:', objeto);
      
      const token = authService.getToken();
      const selectedEmpresa = authService.getSelectedEmpresa();
      
      const dataToSend = {
        ...objeto,
        EmpresaId: selectedEmpresa?.EmpresaId || objeto.EmpresaId
      };
      
      const response = await axios.post(getApiUrl('/api/CatObjeto/guardar'), dataToSend, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Recargar la lista después de guardar
      await this.listar();
      
      return {
        success: true,
        data: response.data,
        message: objeto.ObjetoId && objeto.ObjetoId > 0 ? 'Objeto actualizado exitosamente' : 'Objeto creado exitosamente'
      };
    } catch (error) {
      console.error('Error al guardar objeto:', error);
      throw error;
    }
  }

  // Leer un objeto específico
  async lectura(objetoId) {
    try {
      console.log('Leyendo objeto:', objetoId);
      
      const token = authService.getToken();
      const selectedEmpresa = authService.getSelectedEmpresa();
      
      const response = await axios.post(getApiUrl('/api/CatObjeto/lectura'), {
        ObjetoId: objetoId,
        EmpresaId: selectedEmpresa?.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return {
        success: true,
        data: response.data,
        message: 'Objeto encontrado'
      };
    } catch (error) {
      console.error('Error al leer objeto:', error);
      throw error;
    }
  }

  // Borrar objeto
  async borrar(objetoId) {
    try {
      console.log('Eliminando objeto:', objetoId);
      
      const token = authService.getToken();
      const selectedEmpresa = authService.getSelectedEmpresa();
      
      const response = await axios.post(getApiUrl('/api/CatObjeto/borrar'), {
        ObjetoId: objetoId,
        EmpresaId: selectedEmpresa?.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Recargar la lista después de eliminar
      await this.listar();
      
      return {
        success: true,
        data: response.data,
        message: 'Objeto eliminado exitosamente'
      };
    } catch (error) {
      console.error('Error al eliminar objeto:', error);
      throw error;
    }
  }

  // Recargar objetos
  async recargar() {
    console.log('Recargando objetos...');
    await this.listar();
  }

  // Descargar objetos
  async downloadBulk() {
    try {
      console.log('Descargando objetos...');
      
      const token = authService.getToken();
      const selectedEmpresa = authService.getSelectedEmpresa();
      
      const response = await axios.post(getApiUrl('/api/CatObjeto/descargar'), {
        EmpresaId: selectedEmpresa?.EmpresaId
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'objetos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return {
        success: true,
        message: 'Descarga iniciada'
      };
    } catch (error) {
      console.error('Error al descargar objetos:', error);
      throw error;
    }
  }

  // Ordenar datos
  sort(field) {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
  }

  // Obtener datos ordenados
  get sortedData() {
    if (!this.sortField) return this.objetos;
    
    return [...this.objetos].sort((a, b) => {
      let aVal = a[this.sortField];
      let bVal = b[this.sortField];
      
      // Manejar números
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return this.sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Manejar strings
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return this.sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      return 0;
    });
  }

  // Obtener datos paginados
  get paginatedData() {
    const sorted = this.sortedData;
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return sorted.slice(start, end);
  }

  // Obtener total de páginas
  get totalPages() {
    return Math.ceil(this.objetos.length / this.itemsPerPage);
  }

  // Obtener icono de ordenamiento
  sortIcon(field) {
    if (this.sortField !== field) return 'bi bi-arrow-down-up';
    return this.sortDirection === 'asc' ? 'bi bi-arrow-up' : 'bi bi-arrow-down';
  }

  // Cambiar página
  changePage(page) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  // Verificar si hay empresa seleccionada
  get hasEmpresa() {
    return this.selectedEmpresa && this.selectedEmpresa.EmpresaId;
  }
}
